import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const useCountUp = (target: number, duration = 2000, start = false) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
};

interface StatCardProps {
  value: number;
  suffix: string;
  labelKey: string;
  descKey: string;
  icon: string;
  index: number;
  visible: boolean;
}

const StatCard = ({ value, suffix, labelKey, descKey, icon, index, visible }: StatCardProps) => {
  const { t } = useTranslation();
  const count = useCountUp(value, 1800, visible);
  return (
    <div
      className="relative group text-center p-8 rounded-2xl bg-white border border-gray-100 hover:border-brand-red/30 hover:shadow-xl transition-all duration-500 overflow-hidden"
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="absolute top-0 left-0 w-1 h-full bg-brand-red transform scale-y-0 group-hover:scale-y-100 transition-transform duration-500 origin-top rounded-full rtl:left-auto rtl:right-0"></div>
      <div className="mb-4 flex justify-center">
        <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-red-50 group-hover:bg-brand-red transition-colors duration-300">
          <i className={`${icon} text-brand-red group-hover:text-white text-2xl transition-colors duration-300`}></i>
        </div>
      </div>
      <div className="text-5xl font-black text-gray-900 mb-1 tabular-nums">
        {visible ? count : 0}{suffix}
      </div>
      <div className="font-bold text-gray-800 text-base mb-1">{t(labelKey)}</div>
      <div className="text-gray-400 text-xs">{t(descKey)}</div>
    </div>
  );
};

const Stats = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const statsData = [
    { value: 500, suffix: '+', labelKey: 'stats.clientsLabel', descKey: 'stats.clientsDesc', icon: 'ri-user-smile-line' },
    { value: 1000, suffix: '+', labelKey: 'stats.projectsLabel', descKey: 'stats.projectsDesc', icon: 'ri-checkbox-circle-line' },
    { value: 9, suffix: '+', labelKey: 'stats.yearsLabel', descKey: 'stats.yearsDesc', icon: 'ri-trophy-line' },
    { value: 50, suffix: '+', labelKey: 'stats.countriesLabel', descKey: 'stats.countriesDesc', icon: 'ri-earth-line' },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-12">
          <p className="text-brand-red font-semibold text-sm uppercase tracking-widest mb-2">{t('stats.label')}</p>
          <h2 className="text-4xl font-black text-gray-900">{t('stats.title')}</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {statsData.map((stat, i) => (
            <StatCard key={stat.labelKey} {...stat} index={i} visible={visible} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Stats;
